import React, { useEffect, useMemo, useState } from 'react';
import ConstructionLayout from './ConstructionLayout';
import AerialImageViewer from './AerialImageViewer';
import { useTheme } from '../../contexts/ThemeContext';
import { useModule } from '../../contexts/ModuleContext';
import { Play, Save, Check, X, Image as ImageIcon, Layers, Loader2, AlertCircle, Trash2, Maximize2, CheckCircle2, Database } from 'lucide-react';
import {
  fetchOrthoJobs,
  fetchOrthoJob,
  processOrthoJob,
  saveManualDetections,
  updateDetection,
  reviewDetection,
  deleteOrthoJob,
  rejectSliceImage,
  verifyOrthoJob,
  ingestOrthoJob,
} from '../../services/orthoService';

const STATUS_COLORS = {
  PENDING: '#9CA3AF',
  INITIALIZING: '#F59E0B',
  SLICED: '#3B82F6',
  DETECTING: '#F59E0B',
  DETECTIONS_PENDING_REVIEW: '#8B5CF6',
  VERIFIED: '#10B981',
  ENRICHED: '#10B981',
  COMPLETED: '#059669',
  FAILED: '#EF4444',
};

export default function AerialDataPage() {
  const { isDarkMode } = useTheme();
  const { activeProject } = useModule();

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [section, setSection] = useState('crops'); // 'crops' | 'detections'
  const [manualMode, setManualMode] = useState(false);
  const [manualBoxes, setManualBoxes] = useState([]); // {id, bbox:[gx1,gy1,gx2,gy2], slice, label}
  const [viewer, setViewer] = useState(null); // {slice, mode:'crops'|'detections'|'manual'}
  const [selectedDetId, setSelectedDetId] = useState(null);

  const projectId = activeProject?.numericId || activeProject?.id;

  const card = {
    backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
    border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
    borderRadius: '12px',
  };
  const text = isDarkMode ? '#F3F4F6' : '#111827';
  const subtext = isDarkMode ? '#9CA3AF' : '#6B7280';

  const loadJobs = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      setJobs(await fetchOrthoJobs(projectId));
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setJobs([]);
    setSelectedJobId(null);
    setJobDetail(null);
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const openJob = async (jobId) => {
    setSelectedJobId(jobId);
    setLoading(true);
    setStatusMsg(null);
    try {
      const detail = await fetchOrthoJob(jobId);
      setJobDetail(detail);
      setSection(detail?.detections?.length ? 'detections' : 'crops');
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const refreshJob = async (jobId) => {
    try {
      const detail = await fetchOrthoJob(jobId);
      setJobDetail(detail);
      return detail;
    } catch {
      return null;
    }
  };

  const handleProcess = async () => {
    if (!selectedJobId) return;
    setProcessing(true);
    setStatusMsg({ type: 'info', text: 'Running YOLO detection over slices…' });
    try {
      await processOrthoJob(selectedJobId);
      const poll = setInterval(async () => {
        const detail = await refreshJob(selectedJobId);
        if (detail && ['DETECTIONS_PENDING_REVIEW', 'VERIFIED', 'COMPLETED', 'ENRICHED', 'FAILED'].includes(detail.status)) {
          clearInterval(poll);
          setProcessing(false);
          setSection('detections');
          setStatusMsg(
            detail.status === 'FAILED'
              ? { type: 'error', text: 'Detection failed.' }
              : { type: 'success', text: `Detection complete: ${detail.detections?.length || 0} candidates.` }
          );
        }
      }, 2000);
      setTimeout(() => clearInterval(poll), 1800000);
    } catch (e) {
      setProcessing(false);
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleVerify = async () => {
    if (!selectedJobId) return;
    setVerifying(true);
    try {
      const updated = await verifyOrthoJob(selectedJobId);
      setJobDetail((prev) => ({ ...prev, ...updated }));
      setStatusMsg({ type: 'success', text: 'Job verified successfully! Ready for ingestion into Site Data.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setVerifying(false);
    }
  };

  const handleIngest = async () => {
    if (!selectedJobId) return;
    if (!window.confirm('Ingest all accepted objects from this orthomosaic into project Site Data?')) return;
    setIngesting(true);
    try {
      const res = await ingestOrthoJob(selectedJobId);
      await refreshJob(selectedJobId);
      setStatusMsg({ type: 'success', text: `Successfully ingested ${res.inserted_site_data || 0} items into Site Data!` });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setIngesting(false);
    }
  };

  const handleDelete = async (jobId, name) => {
    if (!window.confirm(`Delete aerial data "${name}"?\nThis removes its crops and detections (rejected false-positive samples are kept for training).`)) return;
    try {
      await deleteOrthoJob(jobId);
      if (String(selectedJobId) === String(jobId)) { setSelectedJobId(null); setJobDetail(null); setManualMode(false); }
      await loadJobs();
      setStatusMsg({ type: 'success', text: 'Aerial data deleted.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleSaveManual = async () => {
    if (!selectedJobId || manualBoxes.length === 0) return;
    try {
      await saveManualDetections(
        selectedJobId,
        manualBoxes.map((b) => ({
          bbox: b.bbox,
          class_name: b.label || 'machine',
          slice_id: b.slice?.id,
        }))
      );
      setManualBoxes([]);
      setManualMode(false);
      setViewer(null);
      await refreshJob(selectedJobId);
      setSection('detections');
      setStatusMsg({ type: 'success', text: 'Manual boxes saved.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleRejectSelected = async () => {
    if (!selectedDetId) return;
    try {
      await reviewDetection(selectedDetId, 'reject', 'false positive');
      setSelectedDetId(null);
      await refreshJob(selectedJobId);
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleAcceptSelected = async () => {
    if (!selectedDetId) return;
    try {
      await reviewDetection(selectedDetId, 'accept');
      setSelectedDetId(null);
      await refreshJob(selectedJobId);
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleRejectImage = async (slice) => {
    if (!window.confirm('Reject this entire image as a false positive?')) return;
    try {
      await rejectSliceImage(slice.id);
      setSelectedDetId(null);
      setViewer(null);
      await refreshJob(selectedJobId);
      setStatusMsg({ type: 'success', text: 'Image rejected and saved.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message });
    }
  };

  const handleBoxUpdate = async (boxId, updates) => {
    if (!viewer) return;
    const s = viewer.slice;

    // Check if it's a draft manual box
    if (String(boxId).startsWith('m')) {
      setManualBoxes((prev) =>
        prev.map((b) => {
          if (b.id !== boxId) return b;
          const next = { ...b };
          if (updates.x1 !== undefined) {
            next.bbox = [
              updates.x1 + s.x_offset,
              updates.y1 + s.y_offset,
              updates.x2 + s.x_offset,
              updates.y2 + s.y_offset,
            ];
          }
          if (updates.label !== undefined) next.label = updates.label;
          return next;
        })
      );
      return;
    }

    // Existing persisted detection
    const patchData = {};
    if (updates.x1 !== undefined) {
      patchData.bbox = [
        updates.x1 + s.x_offset,
        updates.y1 + s.y_offset,
        updates.x2 + s.x_offset,
        updates.y2 + s.y_offset,
      ];
    }
    if (updates.label !== undefined) patchData.class_name = updates.label;

    try {
      await updateDetection(boxId, patchData);
      setJobDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          detections: (prev.detections || []).map((d) => (d.id === boxId ? { ...d, ...patchData } : d)),
        };
      });
    } catch (e) {
      setStatusMsg({ type: 'error', text: `Failed to update detection: ${e.message}` });
    }
  };

  const detectionsBySlice = useMemo(() => {
    const map = {};
    (jobDetail?.detections || []).forEach((d) => {
      const key = d.slice || 'none';
      (map[key] = map[key] || []).push(d);
    });
    return map;
  }, [jobDetail]);

  const detectedSlices = useMemo(() => {
    const slices = jobDetail?.slices || [];
    return slices.filter((s) => detectionsBySlice[s.id]?.length);
  }, [jobDetail, detectionsBySlice]);

  const rejectedSlices = useMemo(() => {
    const slices = jobDetail?.slices || [];
    return slices.filter((s) => s.review_status === 'REJECTED');
  }, [jobDetail]);

  // Viewer boxes (image pixel coords relative to the slice)
  const viewerBoxes = useMemo(() => {
    if (!viewer) return [];
    const s = viewer.slice;
    if (viewer.mode === 'detections') {
      return (detectionsBySlice[s.id] || []).map((d) => ({
        id: d.id,
        x1: d.bbox[0] - s.x_offset, y1: d.bbox[1] - s.y_offset,
        x2: d.bbox[2] - s.x_offset, y2: d.bbox[3] - s.y_offset,
        status: d.status, confidence: d.confidence, label: d.class_name,
      }));
    }
    if (viewer.mode === 'manual') {
      return manualBoxes
        .filter((b) => b.slice.id === s.id)
        .map((b) => ({
          id: b.id,
          x1: b.bbox[0] - s.x_offset, y1: b.bbox[1] - s.y_offset,
          x2: b.bbox[2] - s.x_offset, y2: b.bbox[3] - s.y_offset,
          status: 'ACCEPTED', label: b.label || 'machine',
        }));
    }
    return [];
  }, [viewer, detectionsBySlice, manualBoxes]);

  const btn = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    backgroundColor: bg, color, border: 'none', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  });

  const openViewer = (slice, mode, list) => {
    setSelectedDetId(null);
    setViewer({ slice, mode, list: list || [] });
  };

  const viewerIndex = viewer ? (viewer.list || []).findIndex((s) => s.id === viewer.slice.id) : -1;
  const goPrev = viewer && viewerIndex > 0
    ? () => { setSelectedDetId(null); setViewer((v) => ({ ...v, slice: v.list[viewerIndex - 1] })); }
    : null;
  const goNext = viewer && viewerIndex >= 0 && viewerIndex < (viewer.list || []).length - 1
    ? () => { setSelectedDetId(null); setViewer((v) => ({ ...v, slice: v.list[viewerIndex + 1] })); }
    : null;

  const viewerActions = viewer ? (
    <>
      {viewer.mode === 'detections' && (
        <>
          <button
            onClick={handleRejectSelected}
            disabled={!selectedDetId}
            style={{ ...btn(selectedDetId ? '#EF4444' : '#444', selectedDetId ? '#fff' : '#999'), cursor: selectedDetId ? 'pointer' : 'not-allowed' }}
          >
            <X size={14} /> Reject selected machine
          </button>
          <button onClick={handleAcceptSelected} disabled={!selectedDetId}
            style={{ ...btn(selectedDetId ? '#10B981' : '#444', selectedDetId ? '#fff' : '#999'), cursor: selectedDetId ? 'pointer' : 'not-allowed' }}>
            <Check size={14} /> Accept selected
          </button>
          <button onClick={() => handleRejectImage(viewer.slice)} style={btn('#7F1D1D', '#fff')}>
            <Trash2 size={14} /> Reject whole image
          </button>
        </>
      )}
      {viewer.mode === 'manual' && (
        <button onClick={handleSaveManual} style={btn('#10B981', '#fff')}>
          <Save size={14} /> Save boxes ({manualBoxes.filter((b) => b.slice.id === viewer.slice.id).length})
        </button>
      )}
    </>
  ) : null;

  const onDrawBox = (bboxLocal) => {
    if (!viewer) return;
    const s = viewer.slice;
    const gbox = [bboxLocal[0] + s.x_offset, bboxLocal[1] + s.y_offset, bboxLocal[2] + s.x_offset, bboxLocal[3] + s.y_offset];
    const newId = 'm' + Date.now() + Math.random().toString(36).slice(2, 6);
    setManualBoxes((prev) => [...prev, { id: newId, bbox: gbox, slice: s, label: 'machine' }]);
  };

  const isDetectionDone = jobDetail && ['DETECTIONS_PENDING_REVIEW', 'VERIFIED', 'ENRICHED', 'COMPLETED'].includes(jobDetail.status);

  return (
    <ConstructionLayout title="Aerial Data">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ fontSize: '13px', color: subtext }}>
            {activeProject?.name ? `${activeProject.name} · ` : ''}{jobs.length} upload{jobs.length === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {selectedJobId && (
              <button style={btn(isDarkMode ? '#1E2433' : '#EFF6FF', '#0082F8')}
                onClick={() => { setManualMode((m) => !m); setManualBoxes([]); }}>
                <ImageIcon size={14} /> {manualMode ? 'Cancel Manual' : 'Manual Annotate'}
              </button>
            )}

            {/* Step 3 Process button: disabled/hidden if detection already done */}
            {selectedJobId && !isDetectionDone && (
              <button style={btn('#0082F8', '#FFF')} onClick={handleProcess} disabled={processing}>
                {processing ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
                {processing ? 'Processing…' : 'Process (Detect)'}
              </button>
            )}

            {/* Step 4 Verify Button */}
            {selectedJobId && jobDetail && (jobDetail.status === 'DETECTIONS_PENDING_REVIEW') && (
              <button style={btn('#10B981', '#FFF')} onClick={handleVerify} disabled={verifying}>
                {verifying ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
                {verifying ? 'Verifying…' : 'Mark as Verified'}
              </button>
            )}

            {/* Step 6 Ingest into Site Data Button */}
            {selectedJobId && jobDetail && ['VERIFIED', 'ENRICHED'].includes(jobDetail.status) && (
              <button style={btn('#059669', '#FFF')} onClick={handleIngest} disabled={ingesting}>
                {ingesting ? <Loader2 size={14} className="spin" /> : <Database size={14} />}
                {ingesting ? 'Ingesting…' : 'Ingest to Site Data'}
              </button>
            )}

            {selectedJobId && jobDetail && (
              <button style={btn(isDarkMode ? '#2A1416' : '#FEE2E2', '#EF4444')} onClick={() => handleDelete(selectedJobId, jobDetail.name)}>
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>

        {manualMode && (
          <div style={{ fontSize: '12px', color: '#10B981', background: isDarkMode ? '#0f2a1c' : '#ecfdf5', padding: '8px 12px', borderRadius: '8px' }}>
            Manual mode: click a crop to open it, drag to draw boxes, drag handles to resize/move, click pencil to rename label, then click Save.
          </div>
        )}

        {statusMsg && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px',
            backgroundColor: statusMsg.type === 'error' ? '#7F1D1D22' : statusMsg.type === 'success' ? '#065F4622' : '#1E3A8A22',
            color: statusMsg.type === 'error' ? '#EF4444' : statusMsg.type === 'success' ? '#10B981' : '#60A5FA', fontSize: '13px' }}>
            <AlertCircle size={14} /> {statusMsg.text}
          </div>
        )}

        {/* Album view when no job is selected */}
        {!selectedJobId && (
          <div style={{ ...card, padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: text, marginBottom: '12px' }}>
              <Layers size={16} color="#0082F8" /> Uploaded Orthomosaics
            </div>
            {loading ? (
              <div style={{ color: subtext, fontSize: '13px' }}>Loading…</div>
            ) : jobs.length === 0 ? (
              <div style={{ color: subtext, fontSize: '13px' }}>No aerial data yet. Upload from Project Settings → Upload Data.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
                {jobs.map((job) => (
                  <div key={job.id} onClick={() => openJob(job.id)} style={{ ...card, padding: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'hidden' }}>
                    {/* Thumbnail preview image */}
                    {job.preview_url ? (
                      <div style={{ width: '100%', height: '120px', borderRadius: '8px', overflow: 'hidden', backgroundColor: isDarkMode ? '#0d111a' : '#f3f4f6' }}>
                        <img src={job.preview_url} alt={job.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : (
                      <div style={{ width: '100%', height: '120px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isDarkMode ? '#0d111a' : '#f3f4f6' }}>
                        <ImageIcon size={32} color={subtext} />
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <ImageIcon size={16} color="#0082F8" />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: text, maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                          backgroundColor: `${STATUS_COLORS[job.status] || '#9CA3AF'}22`, color: STATUS_COLORS[job.status] || '#9CA3AF' }}>{job.status}</span>
                        <button title="Delete aerial data" onClick={(e) => { e.stopPropagation(); handleDelete(job.id, job.name); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 0, display: 'flex' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: subtext }}>{job.slice_count || 0} crops · {job.detection_count || 0} detections</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Job detail */}
        {selectedJobId && jobDetail && (
          <div style={{ ...card, padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <button onClick={() => { setSelectedJobId(null); setJobDetail(null); setManualMode(false); }}
                  style={{ background: 'none', border: 'none', color: '#0082F8', cursor: 'pointer', fontSize: '12px', padding: 0 }}>← Back to album</button>
                <div style={{ fontSize: '15px', fontWeight: 700, color: text, marginTop: '4px' }}>{jobDetail.name}</div>
                <div style={{ fontSize: '11px', color: subtext }}>
                  {jobDetail.width}×{jobDetail.height}px · crop {jobDetail.crop_px}px · overlap {Math.round((jobDetail.overlap || 0.15) * 100)}% ·
                  <span style={{ marginLeft: '6px', fontWeight: 700, color: STATUS_COLORS[jobDetail.status] || subtext }}>{jobDetail.status}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  ['overview', 'Overview Preview'],
                  ['crops', `Crops (${jobDetail.slices?.length || 0})`],
                  ['detections', `Detected (${jobDetail.detections?.length || 0})`],
                  ['rejected', `Rejected (${rejectedSlices.length})`],
                ].map(([s, label]) => (
                  <button key={s} onClick={() => setSection(s)}
                    style={btn(section === s ? '#0082F8' : (isDarkMode ? '#1E2433' : '#F3F4F6'), section === s ? '#FFF' : text)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Low-resolution overview view */}
            {section === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '12px' }}>
                {jobDetail.preview_url ? (
                  <div style={{ maxWidth: '900px', width: '100%', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}` }}>
                    <img src={jobDetail.preview_url} alt="Low-res overview" style={{ width: '100%', display: 'block' }} />
                  </div>
                ) : (
                  <div style={{ color: subtext, fontSize: '13px', padding: '32px' }}>No overview preview available.</div>
                )}
                <div style={{ fontSize: '12px', color: subtext }}>
                  Low-resolution overview thumbnail generated for fast viewing.
                </div>
              </div>
            )}

            {/* Crops album — click to open viewer */}
            {section === 'crops' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', maxHeight: '65vh', overflowY: 'auto' }}>
                {(jobDetail.slices || []).map((s) => (
                  <div key={s.id} onClick={() => openViewer(s, manualMode ? 'manual' : 'crops', jobDetail.slices)}
                    style={{ cursor: 'pointer', border: s.review_status === 'REJECTED' ? '2px solid #EF4444' : `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                    <img src={s.image_url} alt={`slice ${s.slice_index}`} style={{ width: '100%', display: 'block' }} />
                    <div style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px' }}>
                      <Maximize2 size={12} color="#fff" />
                    </div>
                    <div style={{ fontSize: '10px', color: subtext, padding: '4px 6px' }}>
                      #{s.slice_index} · {s.width}×{s.height}{s.review_status === 'REJECTED' ? ' · REJECTED' : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Detections — click an image to review its machines */}
            {section === 'detections' && (
              detectedSlices.length === 0 ? (
                <div style={{ color: subtext, fontSize: '13px' }}>
                  {isDetectionDone ? 'No detected objects found for this orthomosaic.' : 'No detections yet. Click "Process (Detect)", or use Manual Annotate.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
                  {detectedSlices.map((s) => {
                    const dets = detectionsBySlice[s.id];
                    const rejected = s.review_status === 'REJECTED';
                    return (
                      <div key={s.id} onClick={() => openViewer(s, 'detections', detectedSlices)}
                        style={{ ...card, padding: '8px', cursor: 'pointer', border: rejected ? '2px solid #EF4444' : `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}` }}>
                        <div style={{ position: 'relative' }}>
                          <img src={s.image_url} alt={`slice ${s.slice_index}`} style={{ width: '100%', display: 'block', borderRadius: '6px', opacity: rejected ? 0.5 : 1 }} />
                          {dets.filter((d) => d.status !== 'REJECTED').map((d) => {
                            const x1 = d.bbox[0] - s.x_offset;
                            const y1 = d.bbox[1] - s.y_offset;
                            const w = d.bbox[2] - d.bbox[0];
                            const h = d.bbox[3] - d.bbox[1];
                            return <div key={d.id} style={{ position: 'absolute', left: `${(x1 / s.width) * 100}%`, top: `${(y1 / s.height) * 100}%`, width: `${(w / s.width) * 100}%`, height: `${(h / s.height) * 100}%`, border: '2px solid #F59E0B', boxSizing: 'border-box' }} />;
                          })}
                          <div style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px' }}><Maximize2 size={12} color="#fff" /></div>
                        </div>
                        <div style={{ fontSize: '11px', color: subtext, marginTop: '4px' }}>
                          #{s.slice_index} · {dets.length} object{dets.length === 1 ? '' : 's'} · {dets.filter((d) => d.status === 'REJECTED').length} rejected
                          {rejected ? ' · IMAGE REJECTED' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* Rejected images list */}
            {section === 'rejected' && (
              rejectedSlices.length === 0 ? (
                <div style={{ color: subtext, fontSize: '13px' }}>No rejected images. Reject a machine (or a whole image) to add it here.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
                  {rejectedSlices.map((s) => (
                    <div key={s.id} onClick={() => openViewer(s, 'detections', rejectedSlices)} style={{ ...card, padding: '8px', border: '2px solid #EF4444', cursor: 'pointer' }}>
                      <img src={s.image_url} alt={`slice ${s.slice_index}`} style={{ width: '100%', display: 'block', borderRadius: '6px', opacity: 0.75 }} />
                      <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px', fontWeight: 600 }}>
                        #{s.slice_index} · REJECTED · {(detectionsBySlice[s.id] || []).length} object{(detectionsBySlice[s.id] || []).length === 1 ? '' : 's'}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {!projectId && (
          <div style={{ ...card, padding: '24px', textAlign: 'center', color: subtext, fontSize: '13px' }}>Select a project to view its aerial data.</div>
        )}
      </div>

      <AerialImageViewer
        open={!!viewer}
        src={viewer?.slice?.image_url}
        imageWidth={viewer?.slice?.width}
        imageHeight={viewer?.slice?.height}
        boxes={viewerBoxes}
        selectedBoxId={selectedDetId}
        onBoxClick={(b) => setSelectedDetId(b.id)}
        drawMode={viewer?.mode === 'manual'}
        onDrawBox={viewer?.mode === 'manual' ? onDrawBox : undefined}
        onBoxUpdate={handleBoxUpdate}
        title={viewer ? `${jobDetail?.name || ''} · crop #${viewer.slice.slice_index} (${viewerIndex + 1}/${(viewer.list || []).length})` : ''}
        actions={viewerActions}
        onPrev={goPrev}
        onNext={goNext}
        onClose={() => { setViewer(null); setSelectedDetId(null); }}
      />
    </ConstructionLayout>
  );
}
